import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  EnvironmentInjector,
  inject,
  OnDestroy,
  OnInit,
  runInInjectionContext,
  untracked,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  LudopathyModal,
  ModalUtil,
  startNativePdfLinkInterceptor,
  UIMenu,
  UIRefresher,
  UITabs,
} from '@bqm-frontend/ui';
import { GlobalStreamingOverlayComponent } from './shared/components/global-streaming-overlay/global-streaming-overlay.component';
import { StreamingService } from './shared/services/streaming.service';
import { IonContent, Platform } from '@ionic/angular';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, take, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CartFacade } from './modules/cart/store';
import { SessionTrackerService, GameType, isAppHomeRoute } from './shared';
import { ROUTING_DATA } from './shared/constants/routing-data.constants';
import { startNativeInternalLinkInterceptor } from './shared/utils/native-internal-link.interceptor';
import { startAndroidBrowserCleanup } from './shared/utils/android-browser-cleanup';
import { PlatformUtil } from './shared/utils/platform/plataform.util';
import { UserSessionService } from './shared/services/user-session/user-session.service';
import { NavInterceptor } from './shared/utils/platform/nav-interceptor.util';
import { SSEConnectionStatus, WSSseService } from './shared/utils/ws-sse-service';
import { SorteoSseHandlerService } from './shared/utils/sorteo-sse-handler.service';
import { UserNotificationsMailboxUnreadService } from './modules/account/services/notifications-mailbox/user-notifications-mailbox-unread.service';
import { UserNotificationsSseService } from './shared/utils/user-notifications-sse.service';
import { CardPaymentFlowService } from './modules/cart/services/card-payment-flow.service';
import { StreamingConfigRuntimeService } from './shared/services/streaming-config/streaming-config-runtime.service';
import { IdDigitalDeepLinkService } from './core/auth/id-digital-deep-link.service';
import { HeyNowChatLoaderService } from './shared/services/heynow-chat/heynow-chat-loader.service';
import { PrivateConfigService } from './shared/services/config-environment/config-environment.service';
import { environment } from 'src/environments/environment';
import { ForceUpdateService } from './shared/services/force-update/force-update.service';
import { EraspaditaPlayModeService } from './shared/services/eraspadita-play-mode.service';

@Component({
  template: `
    @if (showTestingWatermark) {
    <div class="testing-watermark" aria-hidden="true">
      <span class="testing-watermark__label">TESTING</span>
    </div>
    }
    <ion-app>
      <ion-content id="main-ion-content" #mainIonContent>
        <ui-refresher />
        <ui-menu />
        <div class="ion-page pointer-events-none" id="main-content"></div>
        <ui-tabs />
      </ion-content>
      @if (streamingService.isOpen() && !playMode.active()) {

      <app-global-streaming-overlay />

      }
    </ion-app>
  `,
  styles: [
    `
      .testing-watermark {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 100001;
        width: 140px;
        height: 140px;
        overflow: hidden;
        pointer-events: none;
        opacity: 0.7;
      }

      .testing-watermark__label {
        position: absolute;
        top: 28px;
        left: -40px;
        display: block;
        width: 180px;
        padding: 4px 0;
        background: #e53e3e;
        color: #fff;
        font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.08em;
        line-height: 1.2;
        text-align: center;
        transform: rotate(-45deg);
        box-shadow: 0 1px 3px rgb(0 0 0 / 25%);
      }
    `,
  ],
  imports: [UIMenu, UIRefresher, UITabs, GlobalStreamingOverlayComponent],
  providers: [Router],
  selector: 'app-root',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppComponent implements AfterViewInit, OnInit, OnDestroy {
  title = 'bqm-frontend';
  /** Corner ribbon for DEV/STAGING — never shown when environmentName is PROD. */
  readonly showTestingWatermark = environment.environmentName !== 'PROD';
  @ViewChild('mainIonContent') ioncontent!: ElementRef<IonContent>; // Requerido para manejo de scroll programático

  messages: string[] = [];
  readonly destroy$ = new Subject<void>();
  connectionStatus: SSEConnectionStatus = { status: 'disconnected' };
  private readonly streamingConfigRuntime = inject(StreamingConfigRuntimeService);
  private readonly idDigitalDeepLink = inject(IdDigitalDeepLinkService);
  private readonly heyNowChatLoader = inject(HeyNowChatLoaderService);
  private readonly privateConfig = inject(PrivateConfigService);
  private readonly forceUpdate = inject(ForceUpdateService);
  private readonly mailboxUnread = inject(UserNotificationsMailboxUnreadService);
  private readonly sorteoSseHandler = inject(SorteoSseHandlerService);
  private readonly toast = inject(ToastrService);
  readonly playMode = inject(EraspaditaPlayModeService);
  private privateConfigLoaded = false;
  private wasPlaying = false;
  private userNotificationsUsername: string | undefined;
  private userNotificationsSub: Subscription | undefined;
  private expiredBetsSub: Subscription | undefined;
  private stopNativePdfLinkInterceptor: (() => void) | undefined;
  private stopNativeInternalLinkInterceptor: (() => void) | undefined;
  private stopAndroidBrowserCleanup: (() => void) | undefined;

  constructor(
    private readonly navInterceptor: NavInterceptor,
    private readonly platform: Platform,
    private readonly injector: EnvironmentInjector,
    private readonly sessionTracker: SessionTrackerService,
    private readonly modalCtrl: ModalUtil,
    private readonly keycloak: Keycloak,
    readonly sseService: WSSseService,
    readonly userNotificationsSse: UserNotificationsSseService,
    private readonly userSession: UserSessionService,
    private readonly platformUtil: PlatformUtil,
    private readonly cart: CartFacade,
    private readonly cardPaymentFlow: CardPaymentFlowService,
    readonly streamingService: StreamingService,
    private readonly router: Router
  ) {
    effect(() => {
      runInInjectionContext(this.injector, () => {
        const keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
        const keycloakEvent = keycloakSignal();

        if (
          keycloakEvent.type === KeycloakEventType.AuthSuccess ||
          keycloakEvent.type === KeycloakEventType.AuthRefreshSuccess ||
          (keycloakEvent.type === KeycloakEventType.Ready && keycloakEvent.args === true)
        ) {
          this.sessionTracker.calculateSessionDuration(this.showLudopathyAlert);
          if (!untracked(this.playMode.active)) {
            this.mailboxUnread.startWhenAuthenticated();
          }
          this.connectToUserNotifications();
          if (!this.privateConfigLoaded) {
            this.privateConfigLoaded = true;
            this.privateConfig.loadAndApply().pipe(take(1)).subscribe();
          }
        }

        if (
          keycloakEvent.type === KeycloakEventType.AuthLogout ||
          keycloakEvent.type === KeycloakEventType.TokenExpired
        ) {
          this.mailboxUnread.stop();
          this.userNotificationsUsername = undefined;
          this.userNotificationsSub?.unsubscribe();
          this.userNotificationsSub = undefined;
        }
      });
    });

    this.registerStreamingOverlayEffect();
    this.registerPlayModeEffect();
  }

  /** Durante el juego de eRaspadita: SSE, polling del carrito y sonda de Witty en pausa; se reanudan al salir. */
  private registerPlayModeEffect(): void {
    effect(() => {
      const playing = this.playMode.active();
      if (playing === this.wasPlaying) {
        return;
      }
      this.wasPlaying = playing;
      untracked(() => (playing ? this.pauseBackgroundWork() : this.resumeBackgroundWork()));
    });
  }

  private pauseBackgroundWork(): void {
    this.sseService.pause();
    this.userNotificationsSub?.unsubscribe();
    this.userNotificationsSub = undefined;
    this.expiredBetsSub?.unsubscribe();
    this.expiredBetsSub = undefined;
    this.mailboxUnread.pause();
  }

  private resumeBackgroundWork(): void {
    this.sseService.resume();
    this.streamingConfigRuntime.load();
    this.openUserNotificationsStream();
    this.startExpiredBetsCheck();
    this.mailboxUnread.startWhenAuthenticated();
  }

  private startExpiredBetsCheck(): void {
    this.expiredBetsSub?.unsubscribe();
    this.expiredBetsSub = this.cart.validations.checkAndRemoveExpiredBets().pipe(takeUntil(this.destroy$)).subscribe();
  }

  /** Opens/closes the global streaming overlay when CMS catalog state changes (any route). */
  private registerStreamingOverlayEffect(): void {
    effect(() => {
      this.streamingConfigRuntime.loadRevision();
      if (this.streamingConfigRuntime.configs() === null) {
        return;
      }
      const overlay = this.streamingConfigRuntime.homePriorityOverlay();
      if (!overlay) {
        if (this.streamingService.isOpen()) {
          this.streamingService.close();
        }
        return;
      }
      const session = this.streamingService.overlaySessionKey();
      const expected = overlay.type === 'video' ? `v:${overlay.url}` : `p:${overlay.urls.join('|')}`;
      if (!this.streamingService.isOpen() || session !== expected) {
        // No forzar reapertura si el usuario cerró con la X (mismo criterio que effects de juegos).
        if (this.streamingService.autoOpenSuppressedAfterUserClose()) {
          return;
        }
        if (overlay.type === 'video') {
          this.streamingService.openVideo(overlay.url, { sourceGameId: overlay.sourceGameId });
        } else {
          this.streamingService.openPlates(overlay.urls, { sourceGameId: overlay.sourceGameId });
        }
      }
    });
  }

  ngOnInit(): void {
    void this.idDigitalDeepLink.init();
    void this.forceUpdate.checkAndPrompt();
    this.streamingConfigRuntime.load();
    this.stopAndroidBrowserCleanup = startAndroidBrowserCleanup();
    this.stopNativePdfLinkInterceptor = startNativePdfLinkInterceptor((message) => this.toast.error(message));
    this.stopNativeInternalLinkInterceptor = startNativeInternalLinkInterceptor({
      navigate: (url) =>
        this.router.navigateByUrl(url).catch(() => this.router.navigateByUrl(`/inicio?${ROUTING_DATA.NOT_FOUND}=true`)),
    });

    // this.cardPaymentFlow.handleCardPaymentReturn('CART', 100); // testing
    // Monitorear el estado de la conexión
    this.sseService
      .getConnectionStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this.connectionStatus = status;
        if (status.status === 'error') {
          console.log('Estado de conexión SSE:', status);
          console.error('Error en SSE:', status.error);
        }
      });

    // Una sola conexión física: ver WSSseService.messages$
    this.sorteoSseHandler.connect(this.destroy$);

    // inicializar chequeo jugadas vencidas aqui:
    if (!this.playMode.active()) {
      this.startExpiredBetsCheck();
    }

    // Verificar si hay un flujo de pago pendiente
    this.checkCardPaymentIntent();

    // Al volver a Home, permitir que el overlay del CMS se reabra tras cierre/solapa (supresión se limpia en cualquier viewport).
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((e) => {
        if (isAppHomeRoute(e.urlAfterRedirects)) {
          this.streamingService.clearAutoOpenUserSuppress();
        }
        this.streamingService.syncLayoutToCurrentRoute();
      });
  }

  /**
   * Conecta al stream SSE de notificaciones (mismo principio que SorteoSSE: quien conecta queda registrado).
   * No depende del registro en bets-api; info-api identifica por username.
   */
  private connectToUserNotifications() {
    const username =
      (this.keycloak.tokenParsed as any)?.preferred_username ??
      (this.keycloak.tokenParsed as any)?.sub?.split(':')?.pop();
    if (!username) return;

    this.userNotificationsUsername = username;

    // Opcional: registrar sesión en bets-api para otras funcionalidades (no bloquea el SSE).
    this.userSession.register(username, this.userNotificationsSection()).pipe(take(1)).subscribe();

    if (!untracked(this.playMode.active)) {
      this.openUserNotificationsStream();
    }
  }

  private userNotificationsSection(): string {
    return `${this.platformUtil.getTransferOrigin?.() ?? 'LB'}|${globalThis.location?.pathname ?? '/'}`;
  }

  private openUserNotificationsStream(): void {
    const username = this.userNotificationsUsername;
    if (!username) return;

    // El teardown del stream cierra el EventSource activo: desuscribir antes de abrir el nuevo.
    this.userNotificationsSub?.unsubscribe();
    this.userNotificationsSub = this.userNotificationsSse
      .connect(username, this.userNotificationsSection())
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data && data.trim() !== '') {
          console.log('[UserNotify SSE] mensaje recibido:', data);
        }
      });
  }

  ngOnDestroy(): void {
    this.stopNativePdfLinkInterceptor?.();
    this.stopNativePdfLinkInterceptor = undefined;
    this.stopNativeInternalLinkInterceptor?.();
    this.stopNativeInternalLinkInterceptor = undefined;
    this.stopAndroidBrowserCleanup?.();
    this.stopAndroidBrowserCleanup = undefined;
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.navInterceptor.initNavInterceptor({ ioncontent: this.ioncontent });
    document.body.classList.add(this.platformUtil.isIosLikeWeb() ? 'ios' : 'md');
    this.heyNowChatLoader.loadAndInit().catch((err) => console.error('[HeyNow]', err));
  }

  showLudopathyAlert = async () => {
    // El flag local isModalShowing ya fue establecido por SessionTrackerService
    // para prevenir múltiples modales en esta pestaña
    const modal = await this.modalCtrl.create({ component: LudopathyModal });
    await modal.present();
    await modal.onWillDismiss();

    return true;
  };

  private resolveGameTypeFromPaymentReturnUrl(pathname: string, urlParams: URLSearchParams): GameType | 'CART' {
    const pathSegments = pathname.split('/').filter(Boolean);
    const slug = pathSegments.length ? pathSegments[pathSegments.length - 1] : '';
    if (slug === 'cinco-de-oro') {
      return GameType.CINCO_DE_ORO;
    }
    if (slug === 'quiniela') {
      return GameType.QUINIELA_VESPERTINA;
    }
    if (slug === 'tombola') {
      return GameType.TOMBOLA_VESPERTINA;
    }
    if (slug === 'carrito') {
      return 'CART';
    }
    const queryParam = urlParams.get('gameType');
    return queryParam ? (queryParam as GameType) : 'CART';
  }

  private checkCardPaymentIntent(): void {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('card_payment_intent') !== 'true') {
      return;
    }
    const gameTypeParam = this.resolveGameTypeFromPaymentReturnUrl(window.location.pathname, urlParams);
    this.cleanUrl();
    this.cardPaymentFlow.handleCardPaymentReturn(gameTypeParam);
  }

  private cleanUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('card_payment_intent');
    url.searchParams.delete('gameType');

    // Actualizar la URL sin recargar la página
    window.history.replaceState({}, '', url.toString());
  }
}
